import * as React from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import CssBaseline from '@mui/material/CssBaseline';
import FormControlLabel from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import ForgotPassword from './ForgotPassword';
import { GoogleIcon, FacebookIcon } from './CustomIcons';
import AppTheme from '../shared-theme/AppTheme';
import { HandymanAppState } from '../../configuration/redux/reducers';
import { connect } from 'react-redux';
import { refreshToken, signInUser } from '../../pages/Auth/actions';
import { AuthData, SignInErrorType } from '../../pages/Auth/reducer';
import { Button, CircularProgress } from '@mui/material';
import './styles.css';
import { useNavigate } from 'react-router-dom';
import SitemarkIcon from '../marketing-page/components/SitemarkIcon';

interface DispatchFromProps {
  signInUser: (username: string, password: string) => void;
  refreshToken: (refreshToken: string) => void;
}

interface StateFromProps {
  isError: boolean;
  refreshTokenTimeout?: any;
  authData?: AuthData;
  isLoading: boolean;
  errorType?: SignInErrorType;
}

interface PassedProps {
  disableCustomTheme?: boolean;
}

const SignIn = (props: PassedProps & DispatchFromProps & StateFromProps) => {
  const navigate = useNavigate();

  // Local states for errors
  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [fieldChangedAfterLoginAttempt, setFieldChangedAfterLoginAttempt] =
    React.useState(false);

  // ---- NEW: State for "Remember Me" and Email field
  const [rememberMe, setRememberMe] = React.useState(false);
  const [savedEmail, setSavedEmail] = React.useState(''); // The email to display in the input

  // If user is already authenticated, navigate to profile
  React.useEffect(() => {
    if (props.authData) {
      navigate('/profile');
    }
  }, [props.authData, navigate]);

  // ---- NEW: On mount, prefill saved email from localStorage if it exists
  React.useEffect(() => {
    const storedEmail = localStorage.getItem('savedEmail');
    if (storedEmail) {
      setSavedEmail(storedEmail);
      setRememberMe(true); // Mark checkbox as checked if we have a stored email
    }
  }, []);

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  // Called by the form "onSubmit" event
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // If inputs are invalid, do not proceed
    if (emailError || passwordError) {
      return;
    }

    const data = new FormData(event.currentTarget);
    const email = data.get('email') as string;
    const password = data.get('password') as string;

    setFieldChangedAfterLoginAttempt(true);

    // ---- NEW: If rememberMe is checked, store email in localStorage.
    //           Otherwise, remove it.
    if (rememberMe) {
      localStorage.setItem('savedEmail', email);
    } else {
      localStorage.removeItem('savedEmail');
    }

    props.signInUser(email, password);
  };

  // Basic validations
  const validateInputs = () => {
    const emailInput = document.getElementById('email') as HTMLInputElement;
    const passwordInput = document.getElementById('password') as HTMLInputElement;

    let isValid = true;

    if (!emailInput.value || !/\S+@\S+\.\S+/.test(emailInput.value)) {
      setEmailError(true);
      setEmailErrorMessage('Please enter a valid email address.');
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage('');
    }

    if (!passwordInput.value || passwordInput.value.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage('Password must be at least 6 characters long.');
      isValid = false;
    } else if (
      props.errorType === SignInErrorType.incorrectCredentials &&
      !fieldChangedAfterLoginAttempt
    ) {
      setPasswordError(true);
      setPasswordErrorMessage('Incorrect credentials.');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }

    return isValid;
  };

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <SignInContainer direction="column" justifyContent="space-between">
        <Card variant="outlined">
          <SitemarkIcon />
          <Typography
            component="h1"
            variant="h4"
            sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
          >
            Sign in
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: 2,
            }}
          >
            <FormControl>
              <FormLabel htmlFor="email">Email</FormLabel>
              <TextField
                error={emailError}
                helperText={emailErrorMessage}
                id="email"
                type="email"
                name="email"
                value={savedEmail}
                onChange={(e) => setSavedEmail(e.target.value)}
                placeholder="your@email.com"
                autoComplete="email"
                autoFocus
                required
                fullWidth
                variant="outlined"
                color={emailError ? 'error' : 'primary'}
              />
            </FormControl>
            <FormControl>
              <FormLabel htmlFor="password">Password</FormLabel>
              <TextField
                error={passwordError}
                helperText={passwordErrorMessage}
                name="password"
                placeholder="••••••"
                type="password"
                id="password"
                autoComplete="current-password"
                autoFocus
                required
                fullWidth
                variant="outlined"
                color={passwordError ? 'error' : 'primary'}
                onChange={() => setFieldChangedAfterLoginAttempt(true)}
              />
            </FormControl>

            {/** ---- NEW: We control the checkbox via "rememberMe" state */}
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
              }
              label="Remember me"
            />

            <ForgotPassword open={open} handleClose={handleClose} />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              onClick={validateInputs}
              disabled={props.isLoading}
              style={{ position: 'relative' }}
            >
              {props.isLoading ? (
                <CircularProgress
                  size={24}
                  style={{
                    color: 'white',
                    position: 'absolute',
                  }}
                />
              ) : (
                'Sign in'
              )}
            </Button>
            <span className="error-message">
              {props.errorType === SignInErrorType.incorrectCredentials
                ? 'Incorrect credentials'
                : ''}
            </span>
            <Link
              component="button"
              type="button"
              onClick={handleClickOpen}
              variant="body2"
              sx={{ alignSelf: 'center' }}
            >
              Forgot your password?
            </Link>
          </Box>
          <Divider>or</Divider>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => alert('Sign in with Google')}
              startIcon={<GoogleIcon />}
              disabled
            >
              Sign in with Google
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => alert('Sign in with Facebook')}
              startIcon={<FacebookIcon />}
              disabled
            >
              Sign in with Facebook
            </Button>
            <Typography sx={{ textAlign: 'center' }}>
              Don&apos;t have an account?{' '}
              <Link href="sign-up" variant="body2" sx={{ alignSelf: 'center' }}>
                Sign up
              </Link>
            </Typography>
          </Box>
        </Card>
      </SignInContainer>
    </AppTheme>
  );
};

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '450px',
  },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
  height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
  minHeight: '100%',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
  },
}));

const mapStateToProps = (state: HandymanAppState): StateFromProps => {
  return {
    isError: state.user.isError,
    refreshTokenTimeout: state.user.refreshTokenTimeout,
    authData: state.user.authData,
    isLoading: state.user.isLoading,
    errorType: state.user.errorType,
  };
};

export default connect<StateFromProps, DispatchFromProps, any, HandymanAppState>(
  mapStateToProps,
  {
    signInUser: signInUser,
    refreshToken: refreshToken,
  }
)(SignIn);
